// utils/validationUtils.ts
 
export const formatCpfOrCnpj = (value: string): string => {
    if (value === null || value === undefined) return 'Valor inválido para CPF ou CNPJ';

    // CPF é sempre numérico; o novo CNPJ pode conter letras nas 12 primeiras posições
    const documento = limparDocumento(value);

    // Verifica se o valor é um CPF (11 dígitos) ou CNPJ (14 posições alfanuméricas)
    if (documento.length === 11) {
        // Formata CPF: 000.000.000-00
        return documento.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (documento.length === 14) {
        // Formata CNPJ: 00.000.000/0000-00 (aceita letras nas 12 primeiras posições)
        return documento.replace(/^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    } else {
        // Retorna erro ou mensagem caso o valor não tenha o tamanho de CPF ou CNPJ
        return 'Valor inválido para CPF ou CNPJ';
    }
}

/**
 * Remove a máscara de um documento (CPF/CNPJ) mantendo apenas letras e dígitos, em caixa alta.
 * CPF é sempre numérico; o novo CNPJ alfanumérico admite letras nas 12 primeiras posições.
 */
export const limparDocumento = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value.toString().toUpperCase().replace(/[^A-Z0-9]/g, '');
}
 
export const formatCep = (cep: string): string => {
    // Remove qualquer caractere que não seja dígito
    const cleanCep = cep.replace(/\D/g, '');
 
    // Verifica se o CEP tem o tamanho correto (8 dígitos)
    if (cleanCep.length !== 8) {
        return 'CEP inválido';
    }
 
    // Formata o CEP no formato 00000-000
    return cleanCep.replace(/(\d{5})(\d{3})/, '$1-$2');
}
 
export const formatPhoneNumber = (phoneNumber: string): string => {
    const cleanedPhoneNumber = phoneNumber.replace(/\D/g, ''); // Remove todos os caracteres não numéricos
 
    // Verifica se o número tem o formato válido de 10 ou 11 dígitos
    if (cleanedPhoneNumber.length === 10) {
        return cleanedPhoneNumber.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3'); // Formato (XX) XXXX-XXXX
    } else if (cleanedPhoneNumber.length === 11) {
        return cleanedPhoneNumber.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3'); // Formato (XX) XXXXX-XXXX
    }
 
    return phoneNumber;
};
 
export const formatDefaultValuesToString = (value: any): string => {
    if (value === null || value === undefined) return "-";
 
    if (typeof value === "boolean") return value ? "Sim" : "Não";
 
    if (Array.isArray(value)) return renderArray(value);
 
    if (typeof value === "object" && !Array.isArray(value))
        return Object.values(value).join(" - ");
 
    if (isDateValue(value))
        return formatDate(value);
 
    return value.toString();
};
 
export const formatSpecificValuesToString = (key: any, value: any): string => {
    if (value === null || value === undefined) return "-";
 
    if (typeof value === "boolean") return value ? "Sim" : "Não";
 
    if (Array.isArray(value)) return renderArray(value);
 
    if (typeof value === "object" && !Array.isArray(value))
        return formatObjectValues(key, value);
 
    if (isDateValue(value)) {
        if (key === "idade") return calculateAgeToString(value);
        return formatDate(value);
    }
 
    return formatNonObjectValues(key, value);
};
 
const formatNonObjectValues = (key: any, value: any): string => {
    switch (key) {
        case "valor":
        case "valorPendencia":
        case "valorTotal":
        case "valorUltimaOcorrencia":
            return formatValueReais(value);
 
        case "CEP":
            return formatCep(value.toString());
 
        case "DOCUMENTO":
        case "CPFCNPJ":
            return formatCpfOrCnpj(value);
 
        default:
            return value.toString();
    }
};
 
const formatObjectValues = (key: any, value: any): string => {
    switch (key) {
        case "protocolo":
            return [
                `Número: ${value.numero || "N/A"}`,
                `Digito: ${value.digito || "N/A"}`,
            ].join(" - ");
 
        case "operador":
            return [
                `Código: ${value.codigo || "N/A"}`,
                `Nome: ${value.nome || "N/A"}`,
            ].join(" - ");
 
        case "endereco":
            const addressParts = [
                value.logradouro,
                value.numero,
                value.bairro,
                value.complemento,
                `${value.cidade?.estado?.siglaUf || ""} ${value.cidade?.nome || ""}`,
                formatCep(value.cep.toString()),
            ];
            return addressParts
                .filter((part) => part?.toString().trim())
                .join(" - ");
 
        case "telefoneCelular":
        case "telefoneResidencial":
        case "telefoneAssociado":
            return formatPhoneNumber(`${value.numeroDdd || ""}${value.numero || ""}`.trim());
 
        case "situacaoCpf":
            return [
                `Descrição Situação: ${value.descricaoSituacao || "N/A"}`,
                `UF: ${value.uf || "N/A"}`,
                `Data: ${value.dataSituacao ? formatDate(value.dataSituacao) : "N/A"}`,
            ].join(" - ");
 
        case "cpf":
            return [
                `CPF: ${value.numero ? formatCpfOrCnpj(value.numero) : "N/A"}`,
                `Região Origem: ${value.regiaoOrigem || "N/A"}`,
            ].join(" - ");
 
        case "origemAssociado":
        case "cidadeAssociado":
        case "cidade":
            return [
                `${value.estado?.siglaUf || "N/A"}`,
                `${value.nome || "N/A"}`,
            ].join(" - ");
 
        case "moeda":
            return value.simbolo || "-";
 
        default:
            return JSON.stringify(value);
    }
};
 
export const isDateValue = (value: any): boolean => {
    if (value instanceof Date)
        return !isNaN(value.getTime());
 
    if (typeof value === "string") {
        const parsedDate = new Date(value);
        return !isNaN(parsedDate.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(value);
    }
 
    return false;
};
 
export const formatValueReais = (valor: number | string | null | undefined): string => {
    if (valor === null || valor === undefined || (typeof valor === "string" && !valor.trim())) 
        return "R$ 0,00";
 
    let numericValue: number;
 
    if (typeof valor === "string") {
        const valorSemVirgula = valor.replace(",", ".");
        numericValue = parseFloat(valorSemVirgula);
 
        if (isNaN(numericValue)) 
            return "R$ 0,00";
    } else 
        numericValue = valor;
 
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(numericValue);
};
 
export const formatDate = (data: any): string => {
    const date = new Date(data);
    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "long",
        day: "numeric",
    };
 
    return date.toLocaleDateString("pt-BR", options);
};
 
export const calculateAgeToString = (dateOfBirth: any): string => {
    const birth = new Date(dateOfBirth);
    const today = new Date();
 
    let age = today.getFullYear() - birth.getFullYear();
    const month = today.getMonth() - birth.getMonth();
 
    // Se o mês atual for anterior ao mês de nascimento, ou for o mês de nascimento, mas o dia atual ainda não passou, diminui um ano
    if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
 
    return age.toString();
};
 
export const renderArray = (array: any[]): string => {
    return array
        .filter((item) => item !== null && item.toString().trim() !== "")
        .map((item) => (typeof item === "object" ? JSON.stringify(item) : item.toString()))
        .join(", ");
};
 
export const isValidRenavam = (renavam: string) => {
 
    let d: any = renavam.split("");
    let soma: number = 0,
        valor = 0,
        digito = 0,
        x = 0;
 
    for (var i = 5; i >= 2; i--) {
        soma += d[x] * i;
        x++;
    }
 
    valor = soma % 11;
 
    if (valor === 11 || valor === 0 || valor >= 10) {
        digito = 0;
    } else {
        digito = valor;
    }

    if (digito === d[4]) {
        return true;
    } else {
        return false;
    }
}
 
// Validação de CPF
export const isValidCPF = (cpf: string): boolean => {
    cpf = cpf.replace(/[^\d]+/g, ''); // Remove caracteres não numéricos
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
        return false; // CPF inválido se não tiver 11 dígitos ou todos os números forem iguais
    }
 
    let sum: number;
    let rest: number;
    sum = 0;
 
    for (let i = 1; i <= 9; i++) {
        sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    }
 
    rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== parseInt(cpf.substring(9, 10))) return false;
 
    sum = 0;
    for (let i = 1; i <= 10; i++) {
        sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    }
 
    rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== parseInt(cpf.substring(10, 11))) return false;
 
    return true;
};
 
// Validação do novo CNPJ alfanumérico (12 posições alfanuméricas + 2 dígitos verificadores numéricos)
export const isValidCNPJ = (cnpj: string): boolean => {
    if (!cnpj) return false;

    const documento = limparDocumento(cnpj);

    // 12 posições alfanuméricas (A-Z, 0-9) seguidas de 2 dígitos verificadores numéricos
    if (documento.length !== 14) return false;
    if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(documento)) return false;

    // Rejeita documentos com todos os caracteres iguais (ex.: 00000000000000)
    if (/^(.)\1{13}$/.test(documento)) return false;

    // Valor de cada caractere conforme regra da Receita Federal: código ASCII - 48
    // ('0'-'9' => 0-9, 'A'-'Z' => 17-42)
    const valorCaractere = (c: string): number => c.charCodeAt(0) - 48;

    // Calcula um dígito verificador (módulo 11) a partir da base informada
    const calcularDV = (base: string): number => {
        let soma = 0;
        let peso = base.length - 7;
        for (let i = base.length; i >= 1; i--) {
            soma += valorCaractere(base.charAt(base.length - i)) * peso--;
            if (peso < 2) peso = 9;
        }
        const resto = soma % 11;
        return resto < 2 ? 0 : 11 - resto;
    };

    // Primeiro dígito verificador (base = 12 primeiras posições)
    if (calcularDV(documento.substring(0, 12)) !== parseInt(documento.charAt(12), 10)) return false;

    // Segundo dígito verificador (base = 13 primeiras posições)
    if (calcularDV(documento.substring(0, 13)) !== parseInt(documento.charAt(13), 10)) return false;

    return true;
};

// Valida CPF (11 dígitos) ou CNPJ (14 posições alfanuméricas) de acordo com o tamanho
export const isValidCpfCnpj = (value: string): boolean => {
    const documento = limparDocumento(value);
    if (documento.length === 11) return isValidCPF(documento);
    if (documento.length === 14) return isValidCNPJ(documento);
    return false;
};

// Máscara de CPF: 000.000.000-00
export const maskCpf = (value: string): string => {
    let v = limparDocumento(value).replace(/[^0-9]/g, '').slice(0, 11);
    v = v.replace(/(\d{3})(\d)/, '$1.$2');
    v = v.replace(/(\d{3})(\d)/, '$1.$2');
    v = v.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    return v;
};

// Máscara do novo CNPJ alfanumérico: AA.AAA.AAA/AAAA-00
// As 12 primeiras posições aceitam letras/dígitos; os 2 dígitos verificadores são numéricos.
export const maskCnpj = (value: string): string => {
    const limpo = limparDocumento(value);
    const base = limpo.slice(0, 12);                        // 12 posições alfanuméricas
    const dv = limpo.slice(12, 14).replace(/[^0-9]/g, '');  // DV apenas numérico
    let v = base + dv;
    v = v.replace(/^([A-Z0-9]{2})([A-Z0-9])/, '$1.$2');
    v = v.replace(/^([A-Z0-9]{2})\.([A-Z0-9]{3})([A-Z0-9])/, '$1.$2.$3');
    v = v.replace(/\.([A-Z0-9]{3})([A-Z0-9])/, '.$1/$2');
    v = v.replace(/([A-Z0-9]{4})([A-Z0-9])/, '$1-$2');
    return v;
};

// Aplica a máscara adequada conforme o documento seja CPF ou CNPJ.
// CPF é sempre numérico; havendo letras ou mais de 11 posições, trata-se de CNPJ.
export const maskCpfCnpj = (value: string): string => {
    const limpo = limparDocumento(value);
    if (/[A-Z]/.test(limpo) || limpo.length > 11) {
        return maskCnpj(value);
    }
    return maskCpf(value);
};
 
// Validação de e-mail
export const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};
 
// Validação de campo vazio
export const isEmpty = (value: string | null | undefined): boolean => {
    return value === null || value === undefined || value.trim() === '';
};
 
export const openBase64Data = (base64Data: string, dataType: string) => {
    if (!base64Data) return; // Evita erro se o campo estiver vazio
  
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }
    const byteArray = new Uint8Array(byteArrays);
    const blob = new Blob([byteArray], { type: dataType });
  
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
};